import { useMemo, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Home,
  FolderOpen,
  Database,
  Brain,
  Bell,
  ShieldCheck,
  Activity,
  ChevronLeft,
  ChevronRight,
  LogOut,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { LogoIcon, LogoText } from '@/components/ui/Logo';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { useAuth } from '../../lib/auth/provider';
import { hasMinRole, AppRole, AppRoleType } from '../../lib/auth/roles';
import { apiClient } from '../../lib/api/client';
import { CopyJobsProvider } from '../../lib/copy-jobs/context';
import { CopyJobsBadge } from './CopyJobsBadge';

interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  minRole?: AppRoleType;
  end?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/',              labelKey: 'nav.home',          icon: Home,        minRole: undefined, end: true },
  { to: '/datasets',      labelKey: 'nav.datasets',      icon: Database,    minRole: undefined },
  { to: '/projects',      labelKey: 'nav.projects',      icon: FolderOpen,  minRole: undefined },
  { to: '/models',        labelKey: 'nav.models',        icon: Brain,       minRole: AppRole.SUPERADMIN },
  { to: '/notifications', labelKey: 'nav.notifications', icon: Bell,        minRole: undefined },
  { to: '/admin',         labelKey: 'nav.admin',         icon: ShieldCheck, minRole: AppRole.ADMIN },
  { to: '/observability', labelKey: 'nav.observability', icon: Activity,    minRole: AppRole.SUPERADMIN },
];

export function AppShell() {
  const { t } = useTranslation();
  const { logout, profile, session } = useAuth();
  const auth = useMemo(
    () => ({ accessToken: session?.access_token, tenantId: session?.tenant_id ?? undefined }),
    [session],
  );
  const { data: unreadData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => apiClient.getUnreadCount(auth),
    refetchInterval: 15_000,
    enabled: !!session,
  });
  const unreadCount = unreadData?.count ?? 0;

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem('stratifai.sidebar.collapsed') !== 'false';
  });

  const visibleNavItems = useMemo(
    () => NAV_ITEMS.filter((item) => !item.minRole || hasMinRole(profile?.roles ?? [], item.minRole)),
    [profile?.roles],
  );

  function toggleSidebar() {
    setIsCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem('stratifai.sidebar.collapsed', String(next));
      return next;
    });
  }

  return (
    <div className="flex h-screen overflow-hidden bg-surface font-sans">
      {/* ── Fixed sidebar ── */}
      <aside
        className={cn(
          'flex flex-col shrink-0 h-full bg-charcoal border-r border-graphite transition-all duration-300 overflow-hidden',
          isCollapsed ? 'w-20' : 'w-[17rem]',
        )}
      >
        {/* Logo — clickable, routes back to landing. Icon stays at a fixed
            left position; the wordmark slides in with opacity+translate so
            the icon doesn't jump when toggling collapsed state. Matches
            the animation used in the old project's Sidebar.tsx. */}
        <Link
          to="/"
          aria-label={t('app.brand')}
          className="relative flex items-center h-16 shrink-0 border-b border-graphite overflow-hidden px-5 transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
        >
          <LogoIcon className="w-10 h-10 shrink-0 text-white z-10" />
          <LogoText
            aria-hidden={isCollapsed}
            className={cn(
              'absolute left-[68px] h-8 whitespace-nowrap text-white transition-all duration-300 transform origin-left',
              isCollapsed
                ? 'opacity-0 -translate-x-4 pointer-events-none'
                : 'opacity-100 translate-x-0',
            )}
          />
        </Link>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 thin-scrollbar">
          <ul className="flex flex-col gap-0.5">
            {visibleNavItems.map((item) => {
              const isNotifications = item.to === '/notifications';
              const showBadge = isNotifications && unreadCount > 0;
              return (
                <Tooltip key={item.to}>
                  <TooltipTrigger asChild>
                    <li>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) =>
                          cn(
                            'nav-item px-6 overflow-hidden',
                            isActive && 'nav-item-active',
                          )
                        }
                      >
                        <span className="relative shrink-0">
                          <item.icon className="h-5 w-5" />
                          {showBadge && (
                            <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-accent text-[9px] font-semibold text-white leading-none">
                              {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                          )}
                        </span>
                        <span
                          className={cn(
                            'whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out',
                            isCollapsed
                              ? 'max-w-0 opacity-0 ml-0'
                              : 'max-w-[200px] opacity-100 ml-3',
                          )}
                        >
                          {t(item.labelKey)}
                        </span>
                      </NavLink>
                    </li>
                  </TooltipTrigger>
                  {isCollapsed && <TooltipContent side="right">{t(item.labelKey)}</TooltipContent>}
                </Tooltip>
              );
            })}
          </ul>
        </nav>

        {/* Collapse / expand control (bottom of sidebar) */}
        <div className="shrink-0 px-2 pb-2">
          <button
            aria-label={t(isCollapsed ? 'app.nav.expand' : 'app.nav.collapse')}
            onClick={toggleSidebar}
            className="nav-item px-6 w-full overflow-hidden"
          >
            <span className="shrink-0">
              {isCollapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <ChevronLeft className="h-5 w-5" />
              )}
            </span>
            <span
              className={cn(
                'whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out',
                isCollapsed
                  ? 'max-w-0 opacity-0 ml-0'
                  : 'max-w-[200px] opacity-100 ml-3',
              )}
            >
              {t('app.nav.collapse')}
            </span>
          </button>
        </div>

        {/* User / logout — fixed height so expanding/collapsing doesn't
            change the chip's height and push the Collapse button above it.
            Logout is right-anchored via ml-auto so it stays put while the
            name block fades in/out on the left. */}
        {profile && (
          <div className="shrink-0 border-t border-graphite h-20 flex items-center px-6 overflow-hidden">
            <div
              className={cn(
                'min-w-0 overflow-hidden transition-all duration-300 ease-in-out',
                isCollapsed
                  ? 'max-w-0 opacity-0'
                  : 'max-w-[200px] opacity-100',
              )}
            >
              <p className="text-xs font-medium text-white truncate">{profile.display_name}</p>
              {profile.organization && (
                <p className="text-2xs text-ash truncate" data-testid="sidebar-org">
                  {profile.organization}
                </p>
              )}
              <p className="text-2xs text-ash truncate">{profile.roles.join(', ')}</p>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={logout}
                  aria-label={t('app.logout')}
                  className="ml-auto shrink-0 text-ash hover:bg-graphite hover:text-white"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">{t('app.logout')}</TooltipContent>
            </Tooltip>
          </div>
        )}
      </aside>

      {/* ── Main area ── */}
      <CopyJobsProvider auth={auth}>
        <div className="relative flex flex-col flex-1 min-w-0 overflow-hidden">
          {/* Floating copy-jobs badge — lives in the top-right of the
              content area now that the sticky header is gone. */}
          <div className="absolute top-3 right-4 z-10">
            <CopyJobsBadge />
          </div>

          {/* Scrollable page content */}
          <main className="flex-1 overflow-y-auto thin-scrollbar">
            <Outlet />
          </main>
        </div>
      </CopyJobsProvider>
    </div>
  );
}
